import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { getRatingColor, getChartGridColor } from '@t-one-internal/design-system';

// Support both snake_case (API) and camelCase formats
interface RatingCalibrationData {
  manager_id?: string | number;
  managerId?: string | number;
  manager_name?: string;
  managerName?: string;
  rating_1_2?: number;
  rating1_2?: number;
  rating_3_3_5?: number;
  rating3_3_5?: number;
  rating_3_5_4?: number;
  rating3_5_4?: number;
  rating_4_5?: number;
  rating4_5?: number;
  total_employees?: number;
  totalEmployees?: number;
}

interface CycleOption {
  value: string;
  label: string;
}

interface RatingCalibrationWidgetProps {
  data?: RatingCalibrationData | RatingCalibrationData[] | { data: RatingCalibrationData[] };
  loading?: boolean;
  error?: string;
  cycleOptions?: CycleOption[];
  selectedCycleId?: string;
  onCycleChange?: (cycleId: string) => void;
  cycleOptionsLoading?: boolean;
}

// Helper to extract array from various data formats
const extractDataArray = (
  data: RatingCalibrationWidgetProps['data']
): RatingCalibrationData[] => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if ((data as { data: RatingCalibrationData[] })?.data) {
    return (data as { data: RatingCalibrationData[] }).data;
  }
  return [data as RatingCalibrationData];
};

// Helpers for snake_case/camelCase compatibility
const getManagerName = (d: RatingCalibrationData): string =>
  d.managerName ?? d.manager_name ?? 'Unknown';
const getRating1_2 = (d: RatingCalibrationData): number =>
  Number(d.rating1_2 ?? d.rating_1_2 ?? 0);
const getRating3_3_5 = (d: RatingCalibrationData): number =>
  Number(d.rating3_3_5 ?? d.rating_3_3_5 ?? 0);
const getRating3_5_4 = (d: RatingCalibrationData): number =>
  Number(d.rating3_5_4 ?? d.rating_3_5_4 ?? 0);
const getRating4_5 = (d: RatingCalibrationData): number =>
  Number(d.rating4_5 ?? d.rating_4_5 ?? 0);

interface ChartDataPoint {
  managerName: string;
  'rating_1_2': number;
  'rating_3_3_5': number;
  'rating_3_5_4': number;
  'rating_4_5': number;
}

// Rating bucket colors mapped to rating tiers
const COLORS = {
  rating_1_2: getRatingColor(1),     // Low ratings (1–2.5)
  rating_3_3_5: getRatingColor(3),   // Mid ratings (2.5–3.5)
  rating_3_5_4: getRatingColor(4),   // Good ratings (3.5–4)
  rating_4_5: getRatingColor(5),     // Top ratings (4–5)
};

const CustomTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{
    color: string;
    name: string;
    value: number;
    dataKey: string;
  }>;
  label?: string;
}) => {
  if (!active || !payload?.length) return null;

  const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);

  return (
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '12px 14px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        border: '1px solid #E5E7EB',
      }}
    >
      <div
        style={{
          fontSize: '13px',
          fontWeight: 600,
          color: '#374151',
          marginBottom: '8px',
        }}
      >
        {label}
      </div>
      {payload.map((entry) => {
        const tooltipLabels: Record<string, string> = {
          rating_1_2: '1 – 2.5',
          rating_3_3_5: '2.5 – 3.5',
          rating_3_5_4: '3.5 – 4',
          rating_4_5: '4 – 5',
        };
        return (
        <div
          key={entry.dataKey}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '3px 0',
            fontSize: '12px',
          }}
        >
          <span
            style={{
              width: '10px',
              height: '10px',
              borderRadius: '2px',
              backgroundColor: entry.color,
              flexShrink: 0,
            }}
          />
          <span style={{ color: '#6B7280', flex: 1 }}>{tooltipLabels[entry.dataKey] || entry.name}</span>
          <span
            style={{
              color: '#111827',
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {entry.value}
          </span>
        </div>
        );
      })}
      <div
        style={{
          borderTop: '1px solid #E5E7EB',
          marginTop: '6px',
          paddingTop: '6px',
          fontSize: '12px',
          fontWeight: 600,
          color: '#374151',
        }}
      >
        Total: {total}
      </div>
    </div>
  );
};

export const RatingCalibrationWidget: React.FC<RatingCalibrationWidgetProps> = ({
  data,
  loading,
  error,
  cycleOptions,
  selectedCycleId,
  onCycleChange,
  cycleOptionsLoading,
}) => {
  const renderHeader = () => (
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-semibold text-gray-900 text-sm">Rating Calibration View</h3>
      {cycleOptions && cycleOptions.length > 0 && onCycleChange && (
        <select
          value={selectedCycleId || ''}
          onChange={(e) => onCycleChange(e.target.value)}
          disabled={cycleOptionsLoading}
          className="text-xs border border-gray-200 rounded-md px-2 py-1 text-gray-600 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400 w-[200px] truncate"
        >
          {cycleOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      )}
    </div>
  );
  const chartData = useMemo((): ChartDataPoint[] => {
    const rawData = extractDataArray(data);
    console.log('RatingCalibration rawData:', rawData);
    if (!rawData.length) return [];

    const mapped = rawData.map((d) => ({
      managerName: getManagerName(d),
      'rating_1_2': getRating1_2(d),
      'rating_3_3_5': getRating3_3_5(d),
      'rating_3_5_4': getRating3_5_4(d),
      'rating_4_5': getRating4_5(d),
    }));
    console.log('RatingCalibration chartData:', mapped);
    return mapped;
  }, [data]);

  if (loading) {
    return (
      <div className="p-5 h-full flex flex-col">
        {renderHeader()}
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-5 h-full flex flex-col">
        {renderHeader()}
        <div className="flex-1 flex items-center justify-center text-red-500 text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="p-5 h-full flex flex-col">
        {renderHeader()}
        <div className="flex-1 flex items-center justify-center text-gray-400 text-sm text-center px-4">
          No calibration data available. Managers need at least 3 direct reports with completed manager ratings to appear in this view.
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 h-full flex flex-col">
      {/* Header */}
      {renderHeader()}

      {/* Chart */}
      <div style={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
            maxBarSize={48}
          >
            <CartesianGrid
              stroke={getChartGridColor('axis')}
              strokeDasharray="none"
              vertical={false}
            />
            <XAxis
              dataKey="managerName"
              tick={{ fontSize: 11, fill: '#6B7280' }}
              axisLine={{ stroke: '#D1D5DB' }}
              tickLine={false}
              interval={0}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#9CA3AF' }}
              axisLine={{ stroke: '#D1D5DB' }}
              tickLine={false}
              width={30}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
            <Legend
              iconType="square"
              iconSize={10}
              wrapperStyle={{
                fontSize: '11px',
                color: '#374151',
                paddingTop: '8px',
              }}
              formatter={(value: string) => {
                const labels: Record<string, string> = {
                  rating_1_2: '1 – 2.5',
                  rating_3_3_5: '2.5 – 3.5',
                  rating_3_5_4: '3.5 – 4',
                  rating_4_5: '4 – 5',
                };
                return labels[value] || value;
              }}
            />
            <Bar
              dataKey="rating_1_2"
              name="rating_1_2"
              stackId="stack"
              fill={COLORS.rating_1_2}
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="rating_3_3_5"
              name="rating_3_3_5"
              stackId="stack"
              fill={COLORS.rating_3_3_5}
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="rating_3_5_4"
              name="rating_3_5_4"
              stackId="stack"
              fill={COLORS.rating_3_5_4}
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="rating_4_5"
              name="rating_4_5"
              stackId="stack"
              fill={COLORS.rating_4_5}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RatingCalibrationWidget;
